var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import produce from 'immer';
import { cloneDeep, isEqual } from 'lodash';
import * as React from 'react';
import { useEffect, useReducer } from 'react';
import { Button, ButtonMode, ButtonSize, ButtonVariant } from '../Components/Button';
import { ButtonGroup } from '../Components/ButtonGroup';
import { StyledSelect } from '../Components/StyledSelect';
import { MedicationListStatus, MedicationListType } from '../Contracts/MedicationListsSearchFilter.Request';
import { useDebounce } from '../Hooks/useDebounce';
import { ApplicationStorageKeys, loadFromLocalStorage, saveToLocalStorage } from '../Shared/LocalStorage';
import { divider } from '../Styles/Colours';
import styles from './MedicationListFilter.module.css';
var allStatus = { value: 'All', name: 'All' };
var clearFilterDefaults = {
    types: [],
    status: allStatus,
    limit: 50
};
export var isMedListFiltered = function (filter) {
    return !isEqual(filter, clearFilterDefaults);
};
var medListStatuses = [
    allStatus,
    { value: MedicationListStatus.Draft, name: 'Draft' },
    { value: MedicationListStatus.Published, name: 'Published' }
];
var medListTypes = [
    { value: MedicationListType.ScopeOfPracticeList, name: 'Scope of Practice' },
    { value: MedicationListType.Formulary, name: 'Formulary' },
    { value: MedicationListType.DefaultList, name: 'Default' }
];
var filterReducer = produce(function (state, action) {
    switch (action.type) {
        case 'UpdateField':
            if (action.field === 'types' && !action.value)
                state[action.field] = [];
            else
                state[action.field] = action.value;
            break;
        case 'UpdateLimit':
            state.limit = action.value;
            break;
        case 'Clear':
            return cloneDeep(clearFilterDefaults);
    }
});
export function useMedicationListFilter(filterUpdatedCallback) {
    var _a = __read(useReducer(filterReducer, loadFromLocalStorage(ApplicationStorageKeys.MedicationListFilters, cloneDeep(clearFilterDefaults))), 2), state = _a[0], dispatch = _a[1];
    useEffect(function () {
        saveToLocalStorage(ApplicationStorageKeys.MedicationListFilters, state);
    }, [state]);
    var debouncedState = useDebounce(state);
    useEffect(function () {
        filterUpdatedCallback(debouncedState);
    }, [debouncedState]);
    var updateField = function (field) { return function (value) {
        dispatch({ type: 'UpdateField', field: field, value: value });
    }; };
    var updateLimit = function (value) { return function () {
        dispatch({ type: 'UpdateLimit', value: value });
    }; };
    var clearField = function (field, clearValue) { return function () {
        dispatch({ type: 'UpdateField', field: field, value: clearValue });
    }; };
    var clearFilter = function () {
        dispatch({ type: 'Clear' });
    };
    var refresh = function () {
        filterUpdatedCallback(debouncedState);
    };
    var filtered = useDebounce(!isEqual(clearFilterDefaults, debouncedState), 100);
    return {
        state: state,
        filtered: filtered,
        updateField: updateField,
        clearField: clearField,
        updateLimit: updateLimit,
        refresh: refresh,
        clearFilter: clearFilter
    };
}
export function MedicationListFilter(props) {
    return (React.createElement("div", { className: styles.containFilters },
        React.createElement("div", { className: styles.controls },
            React.createElement("div", { className: styles.filterPadding },
                React.createElement("div", { className: styles.filterRow },
                    React.createElement(StyledSelect, { testId: 'type', label: 'Type', itemToString: function (it) { return it.name; }, itemToMenuKey: function (it) { return it.value; }, onSelection: props.updateField('types'), suggestions: medListTypes, value: props.state.types, maxResults: 0, isClearable: true, isMulti: true })),
                React.createElement("div", { className: styles.filterRow },
                    React.createElement(StyledSelect, { testId: 'status', label: 'Status', itemToString: function (it) { return it.name; }, itemToMenuKey: function (it) { return it.value; }, onSelection: props.updateField('status'), suggestions: medListStatuses, value: props.state.status, maxResults: 0, isClearable: false }))),
            React.createElement("div", { className: styles.sectionTitle }, "Show results"),
            React.createElement("div", { className: styles.metricPeriodPicker },
                React.createElement(ButtonGroup, { borderColour: divider },
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 25 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 25 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(25), testId: 'filter-records-25' }, "25"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 50 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 50 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(50), testId: 'filter-records-50' }, "50"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 100 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 100 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(100), testId: 'filter-records-100' }, "100"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 0 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 0 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(0), testId: 'filter-records-all' }, "All")))),
        React.createElement("div", { className: styles.clearButtonContainer },
            React.createElement(Button, { onClick: props.clearFilter, width: '100%', size: ButtonSize.small, variant: ButtonVariant.light, testId: 'clear-filter', color: 'primary' }, "Clear all filters"))));
}
